<template>
  <div>
    <header class="app-bar">
      <app-bar-nav v-if="!isMobile" :user="currentUser"></app-bar-nav>
      <div id="content-start" class="header-user-info">
        <avatar-image :user="profileUser" :width="96" :height="96" />
        <div class="header-user-text">
          <h1>
            <span class="header-user-name">{{ profileUser.displayName }}</span
            ><span class="caption-type">{{ profileUser.username }}</span>
          </h1>
          <p class="header-user-bio">{{ profileUser.bio }}</p>
          <div class="header-user-actions">
            <template v-if="!profileUser.isNative">
              <div class="header-user-my-actions button-list">
                <div class="header-user-role">{{ role }}</div>
                <bridged-a
                  v-if="isOwnProfile"
                  role="button"
                  class="button text-button"
                  :href="profilePage.editProfileUrl"
                  bridge-target="internal"
                >
                  {{ __('Edit profile') }}
                </bridged-a>
              </div>
            </template>
            <template v-else-if="isOwnProfile">
              <div class="header-user-my-actions button-list">
                <div class="header-user-follower-count">{{ profilePage.followerString }}</div>
                <bridged-a
                  role="button"
                  class="button text-button"
                  :href="profilePage.editProfileUrl"
                  bridge-target="internal"
                >
                  {{ __('Edit profile') }}
                </bridged-a>
              </div>
            </template>
            <template v-else-if="!profilePage.isCurrentUserRegistered">
              <a
                role="button"
                :href="profilePage.authUrl"
                class="primary-action-button"
                :title="__('Log in or sign up to follow')"
              >
                {{ __('Follow') }}
              </a>
            </template>
            <template v-else>
              <follow-button
                role="button"
                :status="profilePage.followerStatus"
                :target-user-id="profileUser.id"
                class="primary-action-button"
              />
            </template>
          </div>
        </div>
      </div>
    </header>
    <progress-indeterminate v-if="isLoading" />
    <main class="container">
      <padlet-collection
        v-if="madeWalls.length > 0"
        :walls="madeWalls"
        sort-by="date"
        :title="__('Made')"
      ></padlet-collection>
      <padlet-collection
        v-if="xContributedWalls"
        class="user-profile-contributed"
        :walls="contributedWalls"
        sort-by="date"
        :title="__('Contributed')"
      ></padlet-collection>
      <padlet-collection
        v-if="likedWalls.length > 0 && madeWallsLoading <= 0"
        :walls="likedWalls"
        sort-by="date"
        :title="__('Liked')"
      ></padlet-collection>
      <empty-message v-if="isEmpty">{{ noWallsMessage }}</empty-message>
    </main>
    <MembershipTierUpgradeModal
      v-if="xUpgradeModal && isAppUsing('goldPlansVisible')"
      :user="currentUser"
      @cancel="closePlanUpgradeModal"
    />
    <billing-upgrade-modal
      v-else-if="xUpgradeModal"
      :user="currentUser"
      :upgrade-step="upgradeStep"
      @change-upgrade-step="changeStepPlanUpgradeModal"
      @cancel="closePlanUpgradeModal"
    />
    <GlobalConfirmationDialog />
  </div>
</template>

<script>
import { Component, Vue, Prop } from 'vue-property-decorator'
import BaseIcon from 'vuecomponents/base_icon.vue'
import ModalMixin from 'vuemixins/modal_mixin'
import VueApollo from 'vue-apollo'
import AppBarNav from 'vuecomponents/app_bar_nav'
import FollowButton from 'vuecomponents/follow_button'
import PadletCollection from 'vuecomponents/padlet_collection'
import EmptyMessage from 'vuecomponents/empty_message'
import BridgedA from 'vuecomponents/bridged_a'
import ProgressIndeterminate from 'vuecomponents/progress_indeterminate'
import { apolloProvider } from 'bits/apollo'
import { USER_PADLETS } from 'graphql/padlets'
import window from 'bits/global'
import currentUser from 'bits/current_user'
import device from 'bits/device'
import { captureMessage } from 'bits/error_tracker'
import { translatedRole } from 'bits/user_model'
import AvatarImage from './avatar_image'
import { mapGetters, mapActions } from 'vuex'
import BillingUpgradeModal from 'vuecomponents/billing-upgrade-modal'
import MembershipTierUpgradeModal from 'vuecomponents/MembershipTierUpgradeModal.vue'
import { isAppUsing } from 'bits/flip'
import GlobalConfirmationDialog from 'vuecomponents/GlobalConfirmationDialog.vue'

export default {
  components: {
    AppBarNav,
    AvatarImage,
    BillingUpgradeModal,
    BridgedA,
    EmptyMessage,
    FollowButton,
    GlobalConfirmationDialog,
    MembershipTierUpgradeModal,
    PadletCollection,
    ProgressIndeterminate,
  },
  data: () => ({
    madeWalls: [],
    contributedWalls: [],
    likedWalls: [],
    madeWallsLoading: 0,
    contributedWallsLoading: 0,
    likedWallsLoading: 0,
    currentUser: currentUser,
    profileUser: window.$profileUser,
    profilePage: window.$profilePage,
  }),
  apollo: {
    madeWalls: {
      query: USER_PADLETS,
      variables: function () {
        return {
          userId: this.profileUser.id,
          wallsFilter: 'made',
          shouldFetchLinks: false,
        }
      },
      fetchPolicy: 'cache-and-network',
      loadingKey: 'madeWallsLoading',
      error(error) {
        captureMessage('GraphQL Error for made walls in profile page', {
          context: {
            message: error.message,
          },
        })
      },
      update(data) {
        if (!data.user) {
          return []
        }
        return data.user.walls.edges.map((e) => e.node) || []
      },
    },
    contributedWalls: {
      query: USER_PADLETS,
      variables: function () {
        return {
          userId: this.profileUser.id,
          wallsFilter: 'contributed',
          shouldFetchLinks: false,
        }
      },
      fetchPolicy: 'cache-and-network',
      loadingKey: 'contributedWallsLoading',
      error(error) {
        captureMessage('GraphQL Error for contributed walls in profile page', {
          context: {
            message: error.message,
          },
        })
      },
      update(data) {
        if (!data.user) {
          return []
        }
        return data.user.walls.edges.map((e) => e.node) || []
      },
      skip() {
        return !this.profilePage.showContributedWalls
      },
    },
    likedWalls: {
      query: USER_PADLETS,
      variables: function () {
        return {
          userId: this.profileUser.id,
          wallsFilter: 'liked',
          shouldFetchLinks: false,
        }
      },
      fetchPolicy: 'cache-and-network',
      loadingKey: 'likedWallsLoading',
      error(error) {
        captureMessage('GraphQL Error for liked walls in profiles page', {
          context: {
            message: error.message,
          },
        })
      },
      update(data) {
        if (!data.user) {
          return []
        }
        return data.user.walls.edges.map((e) => e.node) || []
      },
    },
  },
  computed: {
    ...mapGetters('plans', ['xUpgradeModal', 'upgradeStep']),
    ...mapGetters('experiments', [
      'isPlanVisualActive',
      'isPlanVisualUsingNewBasicName',
      'isPlanVisualUsingNewProName',
      'isPlanVisualUsingNewColors',
    ]),
    isMobile: function () {
      return device.mobile
    },
    isEmpty: function () {
      return (
        !this.isLoading &&
        this.madeWalls.length == 0 &&
        this.contributedWalls.length == 0 &&
        this.likedWalls.length == 0
      )
    },
    isLoading: function () {
      return this.madeWallsLoading > 0 || this.contributedWallsLoading > 0 || this.likedWallsLoading > 0
    },
    xContributedWalls: function () {
      // Don't display contributed walls by the gallery user. Lots of folks remake gallery walls... their walls will all appear here
      return !this.isGalleryUser && this.contributedWalls.length > 0 && this.madeWallsLoading <= 0
    },
    noWallsMessage: function () {
      return this.__('%{name} has nothing public to show.', { name: this.profileUser.displayName })
    },
    isOwnProfile: function () {
      return this.currentUser.id === this.profileUser.id
    },
    isGalleryUser: function () {
      return this.profileUser.isGalleryUser
    },
    role: function () {
      return translatedRole(this.profileUser.role)
    },
  },
  methods: {
    ...mapActions('plans', ['changeStepPlanUpgradeModal', 'closePlanUpgradeModal']),
    isAppUsing,
  },
  apolloProvider,
}
</script>

<style lang="scss">
@import '~styles/3/modules/all';

html {
  background-color: $default-background-color;
}

body {
  font-size: 16px;
  line-height: 1.4em;
  min-height: 100vh;
  overflow-wrap: break-word;
}
</style>

<style lang="scss" scoped>
@import '~styles/3/modules/all';

header {
  background-color: white;
}

.header-user-info {
  display: flex;
  align-items: center;
  width: 720px;
  margin: 0px auto;
  padding: 0 24px 32px;
  .avatar {
    flex-shrink: 0;
    @include directional-margin($margin-top: 0, $margin-right: 24px, $margin-bottom: 0, $margin-left: 0);
  }
}

.header-user-text {
  flex-grow: 1;
}

.header-user-role {
  margin-right: 8px;
  text-transform: capitalize;
}

.header-user-actions {
  margin-top: 12px;
}

.header-user-bio {
  line-height: 24px;
  margin-top: 4px;
}

.header-user-name {
  font-size: 24px;
  line-height: 32px;
  font-weight: 500;
  margin-right: 8px;
}

.header-user-follower-count {
  font-size: 0.875rem;
  margin-right: 0.5rem;
}

.header-user-my-actions {
  margin-top: -4px;
  display: flex;
  align-items: center;
}

main.container {
  margin: 16px auto 48px;
}

.user-dropdown-main-avatar {
  margin: 8px 0;
}

@include respondToPhone {
  main.container {
    margin: 0 auto 72px;
  }

  .header-user-info {
    display: block;
    width: 100%;
    padding: 0 16px 24px;
    box-sizing: border-box;
  }

  .header-user-primary {
    display: flex;
    justify-content: center;
    align-items: center;
  }

  .header-user-text {
    flex-grow: 0;
  }

  .header-user-bio {
    margin-top: 16px;
    text-align: center;
  }
}

@include respondToNoDesktop {
  .header-user-info {
    padding-top: 16px;
  }
}
</style>
